"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import "react-toastify/dist/ReactToastify.css";

import useAuth from "@/hooks/useAuth";
import useCredits from "@/hooks/useCredits";
import useProfile from "@/hooks/useProfile";
import { jobDescriptionSchema } from "@/lib/form.types";
import { decreaseCreditCount } from "@/supabase";
import { cn } from "@/utils/conditionClass";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { BsClipboardMinus, BsFillFileEarmarkFill } from "react-icons/bs";
import { ImSpinner2 } from "react-icons/im";
import { Typewriter } from "react-simple-typewriter";
import { ToastContainer, toast } from "react-toastify";
import { z } from "zod";
import PaywallModal from "../PaymentModal";
import Login from "../SigninModal";
import Modal from "../modal";
import ProgressBar, { prepareResults } from "./ServerResponse";
import Loader from "./loading";
import { PopContext } from "@/context/PopContext";

interface AiFormProps {}

export type FormData = z.infer<typeof jobDescriptionSchema>;

// Function to convert Blob to base64
function toBase64(file: any) {
	const reader = new FileReader();
	return new Promise<string>((resolve, reject) => {
		reader.readAsDataURL(file);
		reader.onload = function () {
			resolve(reader.result as string); // Resolve with the base64 data
		};
		reader.onerror = reject;
	});
}

const ResumeForm: FC<AiFormProps> = () => {
	const user = useAuth();
	const credits = useCredits();
	const profile = useProfile();
	const { openNewsLetter, setOpenNewsLetter } = useContext(PopContext);
	const router = useRouter();
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
	} = useForm<FormData>({
		resolver: zodResolver(jobDescriptionSchema),
	});
	const [coverLetter, setCoverLetter] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [jobDetails, setJobDetails] = useState<FormData>();
	const [copied, setCopied] = useState(false);
	const [showPaymentModal, setPaymentModal] = useState(false);
	const [showLoginModal, setLoginModal] = useState(false);
	const [errorState, setErrorState] = useState(false);
	const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string>("");
	const [pdfBlob, setPdfBlob] = useState<string>("");
	const [resumeFile, setResumeFile] = useState<{
		name: string;
		type: string;
		blob: string;
	}>({ name: "", type: "", blob: "" });

	async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		if (!file) {
			return; // No file selected, exit the function
		}
		// Validate PDF file size (limit to 1MB)
		const maxFileSize = 1 * 1024 * 1024; // 1MB in bytes
		if (file.size > maxFileSize) {
			toast.error("PDF file size exceeds the limit of 1MB.");
			return; // Exit the function if the file size is too large
		}
		// Validate PDF file type (allow only PDF files)
		if (file.type !== "application/pdf") {
			toast.error("Only PDF files are allowed.");
			return; // Exit the function if the file type is not PDF
		}
		setPdfPreviewUrl(URL.createObjectURL(file)); // Generate preview URL
		// Encode the file name and type using encodeURIComponent to ensure safe URL parameter values
		const filename = encodeURIComponent(file.name);
		const fileType = encodeURIComponent(file.type);
		setResumeFile((prev) => ({ ...prev, name: filename, type: fileType }));

		// Convert the file to base64
		const pdfBlob = await toBase64(file);
		setPdfBlob(pdfBlob);
	}

	//generate AI Cover letter for user
	const submiteJobDetails = async (
		validatedData: FormData,
		resume_url: string = ""
	) => {
		setErrorState(false);
		if (!profile) return;
		if (credits.credits <= 0 && profile.event === null)
			return setPaymentModal(true);

		const uniqueId = (validatedData.jobCompany + validatedData.jobTitle)
			.trim()
			.toLowerCase();
		const hashedCredit = await fetch(`/api/credits?userID=${profile.id}`);
		const hashedResult = await hashedCredit.json();
		const jobDescription = JSON.stringify({
			jobRequirements: validatedData.jobRequirements,
		});
		let resumeMessage = localStorage.getItem(uniqueId);

		//if the resume message isnt in cache fetch it
		if (!resumeMessage) {
			const resumeresponse = await fetch(
				"https://w5nr8exga8.execute-api.us-east-1.amazonaws.com/dev/resume",
				{
					method: "POST",
					body: JSON.stringify({
						jobDescription,
						aiCredits: hashedResult.credits,
					}),
					headers: {
						"Content-Type": "application/json",
					},
				}
			);
			if (resumeresponse.status === 200) {
				const data = await resumeresponse.json();
				resumeMessage = JSON.stringify(data.message);
				localStorage.setItem(uniqueId, resumeMessage);
				await decreaseCreditCount(user);
			} else if (resumeresponse.status === 402) {
				return setPaymentModal(true);
			} else {
				throw new Error("Server down");
			}
		}

		//if so go straight to analyzing the resume
		const response = await fetch(
			`https://instantapply.co/api/resumeanalyzer`,
			{
				method: "POST",
				body: JSON.stringify({
					resume_url: resume_url || profile?.resume_url,
					hardSkills: {
						message: JSON.parse(resumeMessage),
					},
				}),
				headers: {
					"Content-Type": "application/json",
				},
			}
		);
		if (response.status == 200) {
			const data = await response.json();
			setCoverLetter(data);
			// setTimeout(() => {
			// 	setOpenNewsLetter(true);
			// }, 4000);
		} else {
			throw new Error("server error");
		}
	};

	async function convert(url: string, fileName: string) {
		const res = await fetch(url);
		const blob = await res.blob();
		const file = new File([blob], decodeURIComponent(fileName), {
			type: "application/pdf",
		});
		return file;
	}

	useEffect(() => {
		async function getReview(urlstr: string, parsedDetails: any) {
			setValue("jobCompany", parsedDetails.jobCompany);
			setValue("jobTitle", parsedDetails.jobTitle);
			setValue("jobRequirements", parsedDetails.jobRequirements);
			const file = await convert(urlstr, parsedDetails.fileName.name);
			const pdfBlob = await toBase64(file);
			setPdfBlob(pdfBlob);
			setResumeFile((prev) => ({ ...prev, ...parsedDetails.fileName }));
			const dec = document.querySelector(".chi-file") as any;
			const dataTransfer = new DataTransfer();
			dataTransfer.items.add(file);
			dec.files = dataTransfer.files;
			const event = new Event("change");
			dec.dispatchEvent(event);
			const url = URL.createObjectURL(file);
			setPdfPreviewUrl(url);
		}
		const formDetails = localStorage.getItem("resumeDetails");
		const blobDetail = localStorage.getItem("pdfBlob");
		if (formDetails) {
			const parsedDetails = JSON.parse(formDetails);
			const blobDetails = JSON.parse(blobDetail!);
			getReview(blobDetails, parsedDetails);
		}
	}, []);

	const clearLocalStorage = () => {
		localStorage.removeItem("resumeDetails");
		localStorage.removeItem("pdfBlob");
	};

	async function uploadPDFFile(resumeFile: any, pdfBlob: any) {
		if (!resumeFile.name && !resumeFile.type)
			throw new Error("Please upload a Resume");
		const response = await fetch(
			`/api/upload-pdf?file=${resumeFile.name}&fileType=${resumeFile.type}`,
			{
				method: "POST",
				mode: "cors",
				body: JSON.stringify({ pdfBlob }),
				headers: {
					"Content-Type": "application/json",
				},
			}
		);

		if (!response.ok) {
			throw new Error("Error uploading PDF file");
		}

		const res = await response.json();
		return res.url;
	}

	const onSubmit = async (data: FormData) => {
		setIsLoading(true);
		if (!user) {
			//save data to local storage
			if (!pdfBlob) {
				toast.error("Please Upload Your Resume");
				return setIsLoading(false);
			}

			localStorage.setItem(
				"resumeDetails",
				JSON.stringify({
					jobCompany: data.jobCompany,
					jobTitle: data.jobTitle,
					jobRequirements: data.jobRequirements,
					pdfPreviewUrl: pdfPreviewUrl,
					pdfBlob: pdfBlob,
					fileName: {
						name: resumeFile.name,
						type: resumeFile.type,
					},
				})
			);

			localStorage.setItem("pdfBlob", JSON.stringify(pdfBlob));
			setLoginModal(true);
			return setIsLoading(false);
		}

		if (user) {
			try {
				let validatedData = jobDescriptionSchema.parse(data);
				if (profile?.resume_url) {
					setJobDetails(data);
					await submiteJobDetails(validatedData);
					clearLocalStorage();
					return;
				} else if (pdfBlob) {
					let url;
					url = await uploadPDFFile(resumeFile, pdfBlob);
					setJobDetails(data);
					await submiteJobDetails(validatedData, url);
					clearLocalStorage();
					return;
				}
			} catch (error: any) {
				if (error instanceof z.ZodError) {
					console.log(error);
				} else {
					console.log(error.message);
					toast.error(error);
					setErrorState(true);
				}
			} finally {
				setIsLoading(false);
				clearLocalStorage();
			}
		}
	};
	//copy to clickboard functionality
	const handleCopyClick = async () => {
		if (!coverLetter) return;
		try {
			const [, , unmatched] = prepareResults(coverLetter)!;
			await navigator.clipboard.writeText(unmatched.join("\n"));
			setCopied(true);
			setOpenNewsLetter(true);
		} catch (error) {
			console.error("Failed to copy:", error);
		}
	};

	return (
		<>
			{showLoginModal && (
				<Modal toggleModal={() => setLoginModal(!showLoginModal)}>
					<Login
						slug='resume'
						title='Sign in to view Resume score'
						toggleModal={() => setLoginModal(!showLoginModal)}
					/>
				</Modal>
			)}
			<ToastContainer position='top-center' />
			<div className='flex-col flex md:flex-row md:justify-between gap-5  mt-24 w-11/12 md:max-w-[1200px]  mx-auto  '>
				<div onClick={() => router.back()}>
					<button
						className='text-CEMCS-Blue-100 border border-CEMCS-Blue-100 bg-white hover:bg-white focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800'
						type='submit'
					>
						<svg
							className='inline mr-1'
							width='14'
							height='12'
							viewBox='0 0 14 12'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path d='M1 5.99967H13Z' fill='#1F82BD' />
							<path
								d='M5.66667 10.6663L1 5.99967M1 5.99967L5.66667 1.33301M1 5.99967H13'
								stroke='#1F82BD'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</button>
				</div>
				<div className=' max-w-[500px] p-5 md:p-10 py-5 border border-1 rounded-xl mx-auto  border-gray-100 shadow-sm'>
					<h1 className='text-gray-600 font-bold text-2xl text-center'>
						Resume Analyzer
					</h1>
					<form
						className='max-w-sm lg:min-w-[400px] mx-auto'
						onSubmit={handleSubmit(onSubmit)}
					>
						<div className='my-5 space-y-2'>
							<label
								htmlFor='jobTitle'
								className='block text-sm  leading-6 text-gray-500 '
							>
								Job Title:
							</label>
							<input
								{...register("jobTitle")}
								type='text'
								className='block w-full rounded-lg border-0 p-4 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500  sm:text-md leading-6'
								placeholder='front end developer'
							/>
							<p className='text-sm mt-1 text-red-600'>
								{errors.jobTitle?.message}
							</p>
						</div>
						<div className='my-5 space-y-2'>
							<label
								htmlFor='jobCompany'
								className='block text-sm  leading-6 text-gray-500 '
							>
								Company Name
							</label>

							<input
								{...register("jobCompany")}
								type='text'
								className='block w-full rounded-lg border-0 p-4 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500  sm:text-md leading-6'
								placeholder='google'
							/>
							<p className='text-sm mt-1 text-red-600'>
								{errors.jobCompany?.message}
							</p>
						</div>
						<div className='my-5 space-y-2'>
							<label
								htmlFor='jobRequirements'
								className='block text-sm  leading-6 text-gray-500 '
							>
								Job Requirements
							</label>
							<textarea
								{...register("jobRequirements")}
								className='block w-full rounded-lg border-0 p-4 text-sm  text-gray-600 h-[150px] overflow-y shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-inset focus:ring-blue-500  sm:text-md leading-6'
								placeholder='- proficient with react...'
							/>
							<p className='text-sm mt-1 text-red-600'>
								{errors.jobRequirements?.message}
							</p>
						</div>
						{user && profile?.resume_url ? (
							<div className='my-5 space-y-2'>
								<label
									htmlFor='jobRequirements'
									className='block text-sm  leading-6 text-gray-500 '
								>
									Your resume:
								</label>
								<div className='flex items-center flex-col md:flex-row'>
									{profile?.filename ? (
										<>
											<div className=' items-center inline-flex px-4 py-2 max-w-[250px] rounded-2xl bg-gray-200'>
												<BsFillFileEarmarkFill className='text-gray-400 mr-2' />
												<span className='text-gray-600 text-[12px] truncate w-[150px]'>
													{profile.filename}
												</span>
											</div>
											<Link href='/profile?query=resume'>
												<span className='underline ml-2 text-sm text-gray-500'>
													change resume
												</span>
											</Link>
										</>
									) : (
										<ImSpinner2 className='animate-spin text-gray-300 w-4 h-4 mr-2' />
									)}
								</div>
							</div>
						) : (
							<>
								<label
									htmlFor='file'
									className='block mb-1 text-sm font-medium font-silka text-instant-textdark dark:text-white'
								>
									Input CV <span className='text-red-500 ml-1'>*</span>
								</label>
								<input
									onChange={handleChange}
									accept='.pdf'
									className='chi-file block w-full mb-1 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400'
									id='default_size'
									name='resume_url'
									type='file'
									// value={resumeFile && resumeFile.name}
								/>
								<p className='mb-5 text-sm text-gray-500 dark:text-gray-300'>
									Max file size: 1mb and File type: PDF
								</p>
								{pdfPreviewUrl && (
									<div>
										<iframe
											src={pdfPreviewUrl}
											width='100%'
											height='500'
											title='PDF Preview'
										></iframe>
									</div>
								)}
							</>
						)}
						<button className='active:scale-95 mt-5 inline-flex items-center justify-center rounded-lg text-sm font-medium transition-color focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opactiy-50 disabled:pointer-events-none bg-blue-500 text-white hover:bg-blue-600 w-full py-4 transition-colors'>
							{isLoading ? (
								<ImSpinner2 className='animate-spin text-white w-4 h-4 mr-2' />
							) : (
								<Image
									className='mr-2'
									width={24}
									height={24}
									alt='instant apply logo'
									src='/assets/images/lighting.png'
								/>
							)}
							{`Analyz${isLoading ? "ing" : "e"} Resume`}
						</button>
						{errorState && (
							<small className='mt-4 text-center  block text-red-600'>
								Server Busy Try again Later
							</small>
						)}{" "}
					</form>
				</div>
				<div className='bg-gray-50  max-h-[850px]  shadow-md border-1 border-gray-600 p-5 rounded-lg w-full lg:w-[700px]'>
					{!isLoading ? (
						!coverLetter ? (
							<div className='w-full h-full flex flex-col justify-center items-center'>
								<h1 className='text-lg text-center lg:text-2xl leading-6 tracking-wide text-gray-500 font-semibold mb-5'>
									<Typewriter
										words={[
											"Fuel Your Resume with Keywords: Analyze for Excellence!",
										]}
									/>
								</h1>
								<motion.p
									className='text-lg text-gray-400 '
									initial={{ opacity: 0 }}
									animate={{ opacity: 1, transition: { delay: 6 } }}
								>
									Fill in job details to get started
								</motion.p>
							</div>
						) : (
							<>
								<motion.div
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
								>
									<div className='flexitems-center m-5'>
										<h1 className='text-xl mr-2 font-semibold text-gray-600'>
											{jobDetails?.jobTitle}
										</h1>
										<p className='text-gray-400 font-semibold text-[12px]'>
											{jobDetails?.jobCompany}
										</p>
									</div>
									<div className='w-11/12 min-h-[600px] my-5 mx-auto overflow-y-scroll scrollbar-w-2 scrollbar-thumb-rounded  scrollbar-thumb-blue scrollbar-track-blue-lighter border border-gray-200 rounded-lg px-5 md:px-10 py-5 '>
										<div className='mb-2'>
											<span
												onClick={handleCopyClick}
												className={cn(
													"text-gray-400 group hover:text-gray-600 transition-all group-hover:text-gray-600  text-sm inline-flex items-center cursor-pointer",
													{
														":text-gray-600": copied,
													}
												)}
											>
												<BsClipboardMinus className='mr-1 group-hover:text-gray-600 ' />
												{copied ? "copied" : "copy"} missing key words
											</span>
										</div>
										<span className='w-full flex flex-wrap text-gray-600 text-md'>
											<ProgressBar data={coverLetter} />
										</span>
									</div>
								</motion.div>
							</>
						)
					) : (
						<>
							<div className='w-full h-[600px] text-center text-xl text-gray-400 flex flex-col space-y-40 items-center justify-center'>
								<Loader />
								<Typewriter
									cursorBlinking={true}
									loop={true}
									words={[
										"Every 'no' brings you closer to the perfect 'yes.'",
										"Your skills are valuable; let them shine in your job search.",
										"Be the candidate employers can't resist.",
										"Each application is a step forward, each interview a chance.",
										"Prepare, perform, succeed; you've got this.",
										"Stay positive; the right opportunity is on its way.",
									]}
									cursorStyle='|'
									cursorColor='blue'
								/>
							</div>
						</>
					)}
				</div>
			</div>
			{showPaymentModal && (
				<Modal toggleModal={() => setPaymentModal(!showPaymentModal)}>
					<PaywallModal
						toggleModal={() => setPaymentModal(!showPaymentModal)}
					/>
				</Modal>
			)}
		</>
	);
};

export default ResumeForm;
